import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EXPLORER_API_BASE_URL } from '../config/api';
import { composeQuoteCard, quoteCardIntentUrl, canNativeShareFile } from '../utils/quoteCard';

// Expedition report page (user 07-16) — the click-through target for the
// rail card's grade stamps: the full claw-stamped report card + every field
// note (with scene snapshots) from one expedition.
//
// Data: GET /explorer/journal?session=<started_at> — the same journal the
// viewer's Notes pane reads live. Journals expire after ~30 days, so a
// stamp can outlive its notes; that gets a graceful "faded into legend"
// state rather than an error.

const ACCENT = '#bf94ff';
const KIND_ICON = { bug: '🐛', delight: '✨', idea: '💡', observation: '📝', verdict: '🏁' };
const FEELING_EMOJI = {
  delighted: '🥰', curious: '🧐', impressed: '🤩', confused: '😵‍💫',
  frustrated: '😤', amused: '😄', spooked: '😱', proud: '😤✨',
};

const ExplorerReportPage = () => {
  const { session } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, entries: null, error: false });

  // ── Tomorrow's-destination ballot — same endpoint + localStorage mirror as
  // ClaudeExplorerRailCard, so a vote cast on either surface shows on both.
  // Backend enforces one vote per IP; the mirror just keeps the UI honest.
  const [ballot, setBallot] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const [voteFlash, setVoteFlash] = useState(null);
  const voteFlashTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchBallot = async () => {
      try {
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/vote`);
        if (!res.ok) return;
        const json = await res.json();
        if (cancelled || !json?.ballot) return;
        setBallot(json.ballot);
        try { setMyVote(localStorage.getItem(`claudeExplorerVote:${json.ballot.date}:${json.ballot.rev || ''}`)); } catch (e) { /* private mode */ }
      } catch (e) {
        // Silent — the page just doesn't grow a ballot.
      }
    };
    fetchBallot();
    const id = setInterval(fetchBallot, 20 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  useEffect(() => () => {
    if (voteFlashTimer.current) clearTimeout(voteFlashTimer.current);
  }, []);

  const castVote = async (cid) => {
    if (!ballot || ballot.closed || myVote) return;
    setMyVote(cid); // optimistic — the backend guard is authoritative
    try { localStorage.setItem(`claudeExplorerVote:${ballot.date}:${ballot.rev || ''}`, cid); } catch (e) { /* private mode */ }
    try {
      const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_id: cid }),
      });
      const json = await res.json().catch(() => null);
      if (json?.ballot) setBallot(json.ballot);
      const msg = json?.ok ? json.message : (json?.reason === 'already_voted' ? 'One vote per wanderer! 🗳️' : null);
      if (msg) {
        setVoteFlash(msg);
        if (voteFlashTimer.current) clearTimeout(voteFlashTimer.current);
        voteFlashTimer.current = setTimeout(() => setVoteFlash(null), 4000);
      }
    } catch (e) { /* optimistic state stands; next poll reconciles */ }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/journal?session=${encodeURIComponent(session)}`);
        const json = res.ok ? await res.json() : null;
        if (cancelled) return;
        setState({ loading: false, entries: Array.isArray(json?.entries) ? json.entries : [], error: !json });
      } catch (e) {
        if (!cancelled) setState({ loading: false, entries: [], error: true });
      }
    })();
    return () => { cancelled = true; };
  }, [session]);

  // The Claw Cut (EXPLORER_REPLAY_SPEC): the generated reel, when one
  // exists for this session. Absent = section simply doesn't render.
  const [reel, setReel] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/replay?session=${encodeURIComponent(session)}`);
        const json = res.ok ? await res.json() : null;
        if (!cancelled && json && json.replay && json.replay.v === 1) setReel(json.replay);
      } catch (e) { /* reel is garnish */ }
    })();
    return () => { cancelled = true; };
  }, [session]);

  const entries = state.entries || [];
  const verdict = entries.find((e) => e.kind === 'verdict') || null;
  const notes = entries.filter((e) => e.kind !== 'verdict');
  const bugCount = notes.filter((e) => e.kind === 'bug').length;
  const delightCount = notes.filter((e) => e.kind === 'delight').length;
  // The session id is the expedition's started_at epoch — the date headline.
  const sessionDate = /^\d{10,}$/.test(String(session)) ? new Date(Number(session)) : null;
  const empty = !state.loading && entries.length === 0;
  // Shareable title (user 07-17): the tab/bookmark/pasted-link name should
  // say what this is, not the app's generic title.
  useEffect(() => {
    const prev = document.title;
    const d = /^\d{10,}$/.test(String(session)) ? new Date(Number(session)) : null;
    const bits = ['Claude the Explorer — Expedition Report 🦀'];
    if (d) bits.push(d.toLocaleDateString([], { month: 'short', day: 'numeric' }));
    if (verdict?.grade) bits.push(`Grade ${verdict.grade}`);
    document.title = bits.join(' · ');
    return () => { document.title = prev; };
  }, [session, verdict?.grade]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 📸 Quote cards (SPACES_NEXT_LAYER_SPEC Arc 7 v1) — each field note
  // (and the report card itself) can leave the site as a postcard PNG.
  // Compositing + share plumbing live in utils/quoteCard.js. Flow: native
  // share sheet with the PNG attached on touch devices; otherwise a sheet
  // with the preview, auto clipboard copy, download, and an X intent link.
  const [shareSheet, setShareSheet] = useState(null); // {dataUrl, blob, quote}
  const [shareStatus, setShareStatus] = useState(null); // 'copied' | 'copyfail' | null
  const [shareBusyTs, setShareBusyTs] = useState(null);
  const reportUrl = `${window.location.origin}/explorer/report/${session}`;

  const shareMoment = async (note) => {
    if (shareBusyTs || !note?.entry) return;
    const busyKey = note.ts || 'verdict';
    setShareBusyTs(busyKey);
    try {
      const card = await composeQuoteCard({
        quote: note.entry,
        botName: 'Claude the Explorer',
        grade: verdict?.grade || null,
        kindLabel: note.kind === 'verdict'
          ? 'expedition report'
          : `${KIND_ICON[note.kind] || '📝'} ${note.kind || 'field note'}`,
        dateLabel: sessionDate
          ? sessionDate.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
          : '',
        imageUrl: note.image_url || null,
      });
      const file = new File([card.blob], 'claude-explorer-moment.png', { type: 'image/png' });
      if (canNativeShareFile(file)) {
        try {
          await navigator.share({
            files: [file],
            text: `"${note.entry}" · Claude the Explorer 🦀`,
            url: reportUrl,
          });
          return;
        } catch (e) { /* cancelled — offer the sheet instead */ }
      }
      setShareStatus(null);
      setShareSheet({ dataUrl: card.dataUrl, blob: card.blob, quote: note.entry, kind: note.kind || null });
      // Desktop fast path: the PNG goes straight to the clipboard.
      try {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': card.blob })]);
        setShareStatus('copied');
      } catch (e) { /* clipboard blocked — the sheet buttons still work */ }
    } catch (e) {
      console.error('Quote card failed:', e);
    } finally {
      setShareBusyTs(null);
    }
  };

  const copySheetCard = async () => {
    if (!shareSheet) return;
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': shareSheet.blob })]);
      setShareStatus('copied');
    } catch (e) {
      setShareStatus('copyfail');
    }
  };

  // Copy the report PERMALINK (Nina 07-20: the share sheet carries the
  // shareable webpage, not just the image).
  const copyReportLink = async () => {
    try {
      await navigator.clipboard.writeText(reportUrl);
      setShareStatus('linkcopied');
    } catch (e) {
      setShareStatus('copyfail');
    }
  };

  const ballotCandidates = Array.isArray(ballot?.candidates) ? ballot.candidates : [];
  const ballotTotal = Math.max(1, ballot?.total_votes || 0);
  const ballotWinner = (ballot && ballot.closed && ballot.winner) || null;
  // ⚖️ Dead heat (Nina 07-29): tied leaders get the scales, no 🏆 — the
  // chibi breaks the tie on camera at showtime.
  const ballotTie = (ballot && ballot.closed && !ballot.winner && ballot.tie) || null;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at 50% -10%, #2a1c4d 0%, #17101f 45%, #0d0a12 100%)',
        color: '#ede7ff',
        padding: '28px 16px 64px',
        fontFamily: 'inherit',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Header row — back + title. Right padding clears the global
            LofiRadio FAB pinned top-right (it overlapped the title on
            phone widths). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap', paddingRight: 56 }}>
          <button
            type="button"
            onClick={() => navigate('/explorer/theater')}
            style={{
              padding: '7px 14px',
              borderRadius: 999,
              border: `1.5px solid ${ACCENT}55`,
              background: 'rgba(20, 14, 34, 0.7)',
              color: '#ede7ff',
              fontSize: '0.8rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ← Theater
          </button>
          <div style={{ fontSize: '1.05rem', fontWeight: 900, letterSpacing: '0.06em' }}>
            🦀 EXPEDITION REPORT
          </div>
          {sessionDate && (
            <div style={{ fontSize: '0.8rem', color: '#bbb6c8', fontVariantNumeric: 'tabular-nums' }}>
              {sessionDate.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          )}
        </div>

        {state.loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#bbb6c8' }}>
            Dusting off the field journal…
          </div>
        )}

        {empty && (
          <div
            style={{
              textAlign: 'center',
              padding: '48px 20px',
              borderRadius: 16,
              border: `1.5px dashed ${ACCENT}44`,
              background: 'rgba(20, 14, 34, 0.5)',
            }}
          >
            <div style={{ fontSize: '2rem' }}>🌫️</div>
            <div style={{ fontWeight: 800, marginTop: 6 }}>This expedition has faded into legend.</div>
            <div style={{ color: '#bbb6c8', fontSize: '0.85rem', marginTop: 4 }}>
              Field notes are kept about 30 days. The grade stamp on Claude's shelf remembers it forever.
            </div>
            <button
              type="button"
              onClick={() => navigate('/explorer/theater')}
              style={{
                marginTop: 16,
                padding: '10px 22px',
                borderRadius: 999,
                border: '2px solid #ffd76e',
                background: '#5b3a8e',
                color: '#fff',
                fontWeight: 900,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              Catch the next expedition 🚀
            </button>
          </div>
        )}

        {/* ── The report card — same parchment/claw-stamp language as the
            viewer's grade ceremony, so it reads as the same artifact. ── */}
        {verdict && (
          <div
            style={{
              background: 'linear-gradient(180deg, #fffdf5, #f4ecd7)',
              color: '#3a2f4d',
              border: '3px solid #5b3a8e',
              borderRadius: 14,
              padding: '20px 22px 16px',
              textAlign: 'center',
              boxShadow: '0 12px 48px rgba(0, 0, 0, 0.5)',
              marginBottom: 26,
            }}
          >
            <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.18em', color: '#8a7ba3', textTransform: 'uppercase' }}>
              Expedition Report Card
            </div>
            <div style={{ fontSize: '4.6rem', fontWeight: 900, lineHeight: 1.1, color: '#5b3a8e', textShadow: '0 3px 0 rgba(91, 58, 142, 0.18)' }}>
              {verdict.grade || '?'}
              <span style={{ fontSize: '2rem', verticalAlign: 'super', marginLeft: 4 }}>🦀</span>
            </div>
            <div style={{ fontSize: '0.98rem', fontWeight: 700, margin: '6px auto 10px', maxWidth: '92%' }}>
              “{verdict.entry}”
            </div>
            {(verdict.best_thing || verdict.worst_thing) && (
              <div style={{ fontSize: '0.82rem', textAlign: 'left', margin: '0 auto 10px', maxWidth: '92%' }}>
                {verdict.best_thing && <div style={{ margin: '3px 0' }}>☀️ Best: {verdict.best_thing}</div>}
                {verdict.worst_thing && <div style={{ margin: '3px 0' }}>🌧️ Worst: {verdict.worst_thing}</div>}
              </div>
            )}
            <div style={{ fontSize: '0.78rem', color: '#6f6485' }}>
              {notes.length} field note{notes.length === 1 ? '' : 's'}
              {bugCount > 0 ? ` · ${bugCount} 🐛` : ''}
              {delightCount > 0 ? ` · ${delightCount} ✨` : ''}
            </div>
            <button
              type="button"
              onClick={() => shareMoment(verdict)}
              disabled={!!shareBusyTs}
              style={{
                marginTop: 10,
                padding: '7px 16px',
                borderRadius: 999,
                border: '2px solid #5b3a8e',
                background: 'rgba(91, 58, 142, 0.1)',
                color: '#5b3a8e',
                fontWeight: 900,
                fontSize: '0.78rem',
                cursor: shareBusyTs ? 'default' : 'pointer',
              }}
            >
              {shareBusyTs === (verdict.ts || 'verdict') ? 'Making your card…' : '📸 Share this report'}
            </button>
          </div>
        )}

        {/* ── 📼 The Claw Cut (EXPLORER_REPLAY_SPEC) — the generated
            director's-cut reel, above the raw notes. Beats pair with their
            entries' scene snapshots by ts. Section renders only when a
            reel exists; the honest field notes below are never altered. ── */}
        {reel && (
          <div style={{ marginBottom: 26 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.14em', color: '#ffd76e', textTransform: 'uppercase', marginBottom: 10 }}>
              📼 The Claw Cut
            </div>
            <div style={{ background: 'linear-gradient(180deg, #241640, #1a1030)', border: '2px solid rgba(255,215,110,0.5)', borderRadius: 12, padding: '16px 18px', boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }}>
              <div style={{ fontSize: '1.15rem', fontWeight: 900, color: '#ffd76e', marginBottom: 6 }}>{reel.title}</div>
              <div style={{ fontSize: '0.92rem', color: '#d9cff0', lineHeight: 1.5, marginBottom: 14 }}>{reel.cold_open}</div>
              {/* Scene strip (Nina 07-19: "look like scenes, spread
                  horizontally, images bigger"): each beat = a film-still
                  card — 16:9 scene capture on top (snapshots are 1280x720,
                  so cover crops nothing), SCENE N chip + beat text under
                  it. Row scrolls horizontally inside its own container
                  (never the page) with snap; beats without a capture get a
                  dark marquee filler so the strip keeps its rhythm. */}
              <div style={{ display: 'flex', gap: 12, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollSnapType: 'x proximity', padding: '2px 2px 10px' }}>
                {reel.beats.map((b, i) => {
                  const src = entries.find((e) => e.ts === b.ts) || null;
                  const img = src && src.image_url && /^https:\/\/[a-z0-9.-]+\.amazonaws\.com\//i.test(src.image_url) ? src.image_url : null;
                  return (
                    <div key={b.ts} style={{ flex: '0 0 clamp(250px, 72vw, 330px)', scrollSnapAlign: 'start', display: 'flex', flexDirection: 'column', background: 'rgba(13,10,18,0.55)', border: '1px solid rgba(255,215,110,0.35)', borderRadius: 10, overflow: 'hidden' }}>
                      {img ? (
                        <img src={img} alt={`Scene ${i + 1}`} loading="lazy" style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', display: 'block', borderBottom: '1px solid rgba(255,215,110,0.25)' }} />
                      ) : (
                        <div aria-hidden style={{ width: '100%', aspectRatio: '16 / 9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', background: 'radial-gradient(circle at 50% 30%, #2d1d52, #170f28)', borderBottom: '1px solid rgba(255,215,110,0.25)' }}>🎬</div>
                      )}
                      <div style={{ padding: '10px 12px 12px' }}>
                        <div style={{ fontSize: '0.66rem', fontWeight: 900, letterSpacing: '0.16em', color: '#ffd76e', textTransform: 'uppercase', marginBottom: 5 }}>Scene {i + 1}</div>
                        <div style={{ fontSize: '0.9rem', color: '#ede7ff', lineHeight: 1.5 }}>{b.text}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {reel.closer && (
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed rgba(255,215,110,0.35)', fontSize: '0.92rem', color: '#d9cff0', fontStyle: 'italic', lineHeight: 1.5 }}>{reel.closer}</div>
              )}
            </div>
          </div>
        )}

        {/* ── Field notes — paper slips, oldest first (the day as it
            happened). Scene snapshots ride their notes. ── */}
        {notes.length > 0 && (
          <>
            <div style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.14em', color: '#bbb6c8', textTransform: 'uppercase', marginBottom: 10 }}>
              📓 Field notes
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {notes.map((e, i) => (
                <div
                  key={`${e.ts || i}`}
                  style={{
                    background: 'linear-gradient(180deg, #fffdf5, #f7f0dd)',
                    color: '#3a2f4d',
                    border: '1.5px solid rgba(91, 58, 142, 0.45)',
                    borderRadius: 10,
                    padding: '10px 14px',
                    transform: `rotate(${((i % 3) - 1) * 0.4}deg)`,
                    boxShadow: '0 3px 12px rgba(0,0,0,0.35)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      color: '#8a7ba3',
                      marginBottom: 4,
                      textTransform: 'lowercase',
                    }}
                  >
                    <span>
                      {KIND_ICON[e.kind] || '📝'} {e.kind || 'note'}
                      {e.feeling ? ` · feeling ${e.feeling} ${FEELING_EMOJI[e.feeling] || ''}` : ''}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      {e.ts && (
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {new Date(e.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => shareMoment(e)}
                        disabled={!!shareBusyTs}
                        title="Share this moment"
                        style={{
                          padding: '2px 9px',
                          borderRadius: 999,
                          border: '1px solid rgba(91, 58, 142, 0.45)',
                          background: 'rgba(91, 58, 142, 0.08)',
                          color: '#5b3a8e',
                          fontSize: '0.68rem',
                          fontWeight: 800,
                          cursor: shareBusyTs ? 'default' : 'pointer',
                          textTransform: 'lowercase',
                        }}
                      >
                        {shareBusyTs === e.ts ? '…' : '📸 share'}
                      </button>
                    </span>
                  </div>
                  <div style={{ fontSize: '0.9rem', lineHeight: 1.45 }}>{e.entry}</div>
                  {e.image_url && (
                    <img
                      src={e.image_url}
                      alt="Scene from the expedition"
                      loading="lazy"
                      style={{
                        display: 'block',
                        width: '100%',
                        maxWidth: '100%',
                        marginTop: 8,
                        borderRadius: 8,
                        border: '1.5px solid rgba(91, 58, 142, 0.35)',
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Tomorrow's-destination ballot — vote in place, same ballot the
            viewer and rail card serve. Hidden entirely when no ballot is up
            (voting opens after departure). ── */}
        {!state.loading && ballotCandidates.length > 0 && (
          <div
            style={{
              marginTop: 32,
              padding: '18px 18px 14px',
              borderRadius: 16,
              border: `1.5px solid ${ACCENT}55`,
              background: 'rgba(20, 14, 34, 0.7)',
            }}
          >
            <div style={{ fontSize: '0.95rem', fontWeight: 900, letterSpacing: '0.04em', textAlign: 'center' }}>
              🗳️ Where should Claude go next?
            </div>
            <div style={{ fontSize: '0.78rem', color: '#bbb6c8', textAlign: 'center', margin: '4px 0 14px' }}>
              {ballot.closed
                ? 'Ballot closed. He packs his satchel!'
                : (myVote ? 'Vote in! Watch the race below.' : 'Tap a destination. One vote per wanderer.')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {ballotCandidates.map((c) => {
                const pct = Math.round(((c.votes || 0) / ballotTotal) * 100);
                const isMine = myVote === c.id;
                const isWinner = ballotWinner && ballotWinner.id === c.id;
                const isTied = ballotTie && ballotTie.leaders.some((l) => l.id === c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => castVote(c.id)}
                    disabled={ballot.closed || !!myVote}
                    style={{
                      position: 'relative',
                      overflow: 'hidden',
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: `1.5px solid ${isMine || isWinner || isTied ? '#ffd76e' : `${ACCENT}44`}`,
                      background: 'rgba(13, 10, 18, 0.6)',
                      color: '#ede7ff',
                      cursor: ballot.closed || myVote ? 'default' : 'pointer',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: `${pct}%`,
                        background: isMine || isWinner || isTied ? 'rgba(255, 215, 110, 0.18)' : `${ACCENT}22`,
                        transition: 'width 600ms ease',
                      }}
                    />
                    <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: '0.88rem', fontWeight: 800 }}>
                      <span>
                        {c.emoji ? `${c.emoji} ` : ''}{c.label}
                        {isWinner ? ' 🏆' : isTied ? ' ⚖️' : (isMine ? ' ✓' : '')}
                      </span>
                      <span style={{ color: '#bbb6c8', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                        {c.votes || 0} · {pct}%
                      </span>
                    </div>
                    {c.blurb && (
                      <div style={{ position: 'relative', fontSize: '0.75rem', color: '#bbb6c8', marginTop: 2 }}>
                        {c.blurb}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {ballotTie && (
              <div style={{ textAlign: 'center', fontSize: '0.8rem', color: '#ffd76e', fontWeight: 800, marginTop: 10 }}>
                ⚖️ Dead heat! Chibi breaks the tie live on stream.
              </div>
            )}
            {voteFlash && (
              <div style={{ textAlign: 'center', fontSize: '0.8rem', color: '#ffd76e', fontWeight: 800, marginTop: 10 }}>
                {voteFlash}
              </div>
            )}
          </div>
        )}

        {/* Footer CTA — keep the loop going */}
        {!state.loading && !empty && (
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <button
              type="button"
              onClick={() => navigate('/explorer/theater')}
              style={{
                padding: '12px 26px',
                borderRadius: 999,
                border: '2px solid #ffd76e',
                background: '#5b3a8e',
                color: '#fff',
                fontWeight: 900,
                fontSize: '0.9rem',
                cursor: 'pointer',
                boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
              }}
            >
              Catch the next expedition 🚀
            </button>
          </div>
        )}
      </div>

      {/* ── 📸 Share sheet — card preview + copy / download / X. Fixed
          overlay, fluid width (no media queries, per the page's rule). ── */}
      {shareSheet && (
        <div
          onClick={() => setShareSheet(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            background: 'rgba(10, 7, 16, 0.78)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(ev) => ev.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 560,
              maxHeight: '92vh',
              overflowY: 'auto',
              background: '#1a1030',
              border: `1.5px solid ${ACCENT}66`,
              borderRadius: 16,
              padding: 16,
              boxShadow: '0 18px 60px rgba(0, 0, 0, 0.6)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontWeight: 900, fontSize: '0.95rem' }}>{shareSheet.kind === 'verdict' ? '📸 Share this report' : '📸 Share this moment'}</div>
              <button
                type="button"
                onClick={() => setShareSheet(null)}
                aria-label="Close"
                style={{ border: 'none', background: 'transparent', color: '#bbb6c8', fontSize: '1.05rem', fontWeight: 800, cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
            <img
              src={shareSheet.dataUrl}
              alt="Postcard of this expedition moment"
              style={{ display: 'block', width: '100%', borderRadius: 10, border: `1px solid ${ACCENT}44` }}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={copySheetCard}
                style={{
                  padding: '9px 18px',
                  borderRadius: 999,
                  border: '2px solid #ffd76e',
                  background: '#5b3a8e',
                  color: '#fff',
                  fontWeight: 900,
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                }}
              >
                Copy image
              </button>
              <a
                href={shareSheet.dataUrl}
                download={`claude-expedition-${session}.png`}
                style={{
                  padding: '9px 18px',
                  borderRadius: 999,
                  border: `1.5px solid ${ACCENT}66`,
                  background: 'rgba(20, 14, 34, 0.7)',
                  color: '#ede7ff',
                  fontWeight: 800,
                  fontSize: '0.82rem',
                  textDecoration: 'none',
                }}
              >
                Download
              </a>
              <a
                href={quoteCardIntentUrl(shareSheet.quote, reportUrl)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '9px 18px',
                  borderRadius: 999,
                  border: `1.5px solid ${ACCENT}66`,
                  background: 'rgba(20, 14, 34, 0.7)',
                  color: '#ede7ff',
                  fontWeight: 800,
                  fontSize: '0.82rem',
                  textDecoration: 'none',
                }}
              >
                Post on X
              </a>
              <button
                type="button"
                onClick={copyReportLink}
                style={{
                  padding: '9px 18px',
                  borderRadius: 999,
                  border: `1.5px solid ${ACCENT}66`,
                  background: 'rgba(20, 14, 34, 0.7)',
                  color: '#ede7ff',
                  fontWeight: 800,
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                }}
              >
                Copy link
              </button>
            </div>
            <div style={{ fontSize: '0.78rem', color: shareStatus === 'copyfail' ? '#ff9d9d' : '#ffd76e', fontWeight: 800, marginTop: 10, minHeight: 18 }}>
              {shareStatus === 'copied' ? 'Copied! Paste it anywhere.' : ''}
              {shareStatus === 'linkcopied' ? 'Link copied! Send it around.' : ''}
              {shareStatus === 'copyfail' ? 'Copy blocked. Grab the download.' : ''}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExplorerReportPage;
